
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Divider,
} from 'antd';

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
  EyeOutlined,
  CloseOutlined,
} from '@ant-design/icons';

import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PAGE_SIZE = 20;

const CustomerManagement = () => {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);

  const [duplicates, setDuplicates] = useState({
    byName: [],
    byTin: [],
    byPhone: [],
  });

  const [loading, setLoading] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  const [activeTab, setActiveTab] = useState('duplicates');

  const [duplicatePage, setDuplicatePage] = useState(1);
  const [duplicateSearch, setDuplicateSearch] = useState('');
  const [duplicateType, setDuplicateType] = useState('all');
  const [duplicateSize, setDuplicateSize] = useState('all');

  const [expandedGroups, setExpandedGroups] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [compareVisible, setCompareVisible] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const [form] = Form.useForm();

  /*
   * --------------------------------------------------------------------------
   * Fetch customers
   * --------------------------------------------------------------------------
   */

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get('/api/customers');

      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch customers:', err);

      messageApi.error({
        content: 'Failed to fetch customers',
        key: 'customers-fetch-error',
      });
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  /*
   * --------------------------------------------------------------------------
   * Fetch duplicate audit
   * --------------------------------------------------------------------------
   */

  const fetchDuplicates = useCallback(
    async (showError = true) => {
      try {
        setDuplicateLoading(true);

        const res = await api.get('/api/customers/duplicates');

        const data = res.data || {};

        setDuplicates({
          byName: Array.isArray(data.byName) ? data.byName : [],
          byTin: Array.isArray(data.byTin) ? data.byTin : [],
          byPhone: Array.isArray(data.byPhone) ? data.byPhone : [],
        });
      } catch (err) {
        console.error('Failed to fetch duplicates:', err);

        if (showError) {
          messageApi.error({
            content: 'Failed to load duplicate audit',
            key: 'duplicates-fetch-error',
          });
        }
      } finally {
        setDuplicateLoading(false);
      }
    },
    [messageApi]
  );

  useEffect(() => {
    fetchCustomers();
    fetchDuplicates(false);
  }, [fetchCustomers, fetchDuplicates]);

  /*
   * --------------------------------------------------------------------------
   * Customer CRUD
   * --------------------------------------------------------------------------
   */

  const handleCreate = () => {
    setEditingCustomer(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCustomer(record);

    form.setFieldsValue({
      name: record.name || '',
      tin: record.tin || '',
      phone: record.phone || '',
    });

    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/customers/${id}`);

      messageApi.success('Customer deleted successfully');

      await Promise.all([
        fetchCustomers(),
        fetchDuplicates(false),
      ]);
    } catch (err) {
      console.error('Failed to delete customer:', err);

      messageApi.error('Failed to delete customer');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCustomer) {
        await api.put(
          `/api/customers/${editingCustomer.id}`,
          values
        );

        messageApi.success('Customer updated successfully');
      } else {
        await api.post('/api/customers', values);

        messageApi.success('Customer created successfully');
      }

      setModalVisible(false);
      setEditingCustomer(null);
      form.resetFields();

      await Promise.all([
        fetchCustomers(),
        fetchDuplicates(false),
      ]);
    } catch (err) {
      console.error('Customer save error:', err);

      messageApi.error(
        editingCustomer
          ? 'Failed to update customer'
          : 'Failed to create customer'
      );
    }
  };

  /*
   * --------------------------------------------------------------------------
   * Normalize duplicate values
   * --------------------------------------------------------------------------
   */

  const normalizeValue = (value) => {
    if (!value) return '';

    return String(value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  };

  /*
   * --------------------------------------------------------------------------
   * Build duplicate groups
   *
   * Converts:
   *
   * byName
   * byTin
   * byPhone
   *
   * into grouped records:
   *
   * {
   *   key,
   *   type,
   *   value,
   *   records
   * }
   * --------------------------------------------------------------------------
   */

  const duplicateGroups = useMemo(() => {
    const groups = new Map();

    const addGroup = (type, value, records) => {
      const normalized = normalizeValue(value);

      if (!normalized || !Array.isArray(records)) {
        return;
      }

      const key = `${type}:${normalized}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          type,
          value,
          records: [],
        });
      }

      const existing = groups.get(key);

      records.forEach((record) => {
        if (!record || !record.id) return;

        const alreadyExists = existing.records.some(
          (item) => item.id === record.id
        );

        if (!alreadyExists) {
          existing.records.push(record);
        }
      });
    };

    (duplicates.byName || []).forEach((record) => {
      addGroup(
        'name',
        record.name,
        duplicates.byName.filter(
          (item) =>
            normalizeValue(item.name) ===
            normalizeValue(record.name)
        )
      );
    });

    (duplicates.byTin || []).forEach((record) => {
      if (!record.tin) return;

      addGroup(
        'tin',
        record.tin,
        duplicates.byTin.filter(
          (item) =>
            normalizeValue(item.tin) ===
            normalizeValue(record.tin)
        )
      );
    });

    (duplicates.byPhone || []).forEach((record) => {
      if (!record.phone) return;

      addGroup(
        'phone',
        record.phone,
        duplicates.byPhone.filter(
          (item) =>
            normalizeValue(item.phone) ===
            normalizeValue(record.phone)
        )
      );
    });

    return Array.from(groups.values())
      .filter((group) => group.records.length > 1)
      .sort((a, b) => {
        if (b.records.length !== a.records.length) {
          return b.records.length - a.records.length;
        }

        return String(a.value).localeCompare(
          String(b.value)
        );
      });
  }, [duplicates]);

  /*
   * --------------------------------------------------------------------------
   * Statistics
   * --------------------------------------------------------------------------
   */

  const uniqueDuplicateRecordIds = useMemo(() => {
    const ids = new Set();

    duplicateGroups.forEach((group) => {
      group.records.forEach((record) => {
        ids.add(record.id);
      });
    });

    return ids;
  }, [duplicateGroups]);

  const totalDuplicateGroups = duplicateGroups.length;

  const totalAffectedRecords = uniqueDuplicateRecordIds.size;

  const nameDuplicateGroups = duplicateGroups.filter(
    (group) => group.type === 'name'
  ).length;

  const tinDuplicateGroups = duplicateGroups.filter(
    (group) => group.type === 'tin'
  ).length;

  const phoneDuplicateGroups = duplicateGroups.filter(
    (group) => group.type === 'phone'
  ).length;

  /*
   * --------------------------------------------------------------------------
   * Search / filtering
   * --------------------------------------------------------------------------
   */

  const filteredDuplicateGroups = useMemo(() => {
    let result = [...duplicateGroups];

    if (duplicateType !== 'all') {
      result = result.filter(
        (group) => group.type === duplicateType
      );
    }

    if (duplicateSize !== 'all') {
      if (duplicateSize === '2') {
        result = result.filter(
          (group) => group.records.length === 2
        );
      }

      if (duplicateSize === '3+') {
        result = result.filter(
          (group) => group.records.length >= 3
        );
      }
    }

    const search = normalizeValue(duplicateSearch);

    if (search) {
      result = result.filter((group) => {
        if (
          normalizeValue(group.value).includes(search)
        ) {
          return true;
        }

        return group.records.some((record) => {
          return (
            normalizeValue(record.name).includes(search) ||
            normalizeValue(record.tin).includes(search) ||
            normalizeValue(record.phone).includes(search) ||
            String(record.id).includes(search)
          );
        });
      });
    }

    return result;
  }, [
    duplicateGroups,
    duplicateType,
    duplicateSize,
    duplicateSearch,
  ]);

  const duplicateTotalPages = Math.ceil(
    filteredDuplicateGroups.length / PAGE_SIZE
  );

  const paginatedDuplicateGroups = useMemo(() => {
    const start =
      (duplicatePage - 1) * PAGE_SIZE;

    return filteredDuplicateGroups.slice(
      start,
      start + PAGE_SIZE
    );
  }, [
    filteredDuplicateGroups,
    duplicatePage,
  ]);

  useEffect(() => {
    if (
      duplicatePage > duplicateTotalPages &&
      duplicateTotalPages > 0
    ) {
      setDuplicatePage(1);
    }
  }, [
    duplicatePage,
    duplicateTotalPages,
  ]);

  /*
   * --------------------------------------------------------------------------
   * Determine most complete customer
   * --------------------------------------------------------------------------
   */

  const getCompletenessScore = (customer) => {
    if (!customer) return 0;

    let score = 0;

    if (customer.name?.trim()) score += 1;
    if (customer.tin?.trim()) score += 3;
    if (customer.phone?.trim()) score += 2;

    if (customer.email?.trim()) score += 2;
    if (customer.address?.trim()) score += 2;
    if (customer.city?.trim()) score += 1;
    if (customer.country?.trim()) score += 1;
    if (customer.contactPerson?.trim()) score += 1;

    return score;
  };

  const getMostCompleteRecord = (records) => {
    if (!records?.length) return null;

    return [...records].sort((a, b) => {
      const scoreDifference =
        getCompletenessScore(b) -
        getCompletenessScore(a);

      if (scoreDifference !== 0) {
        return scoreDifference;
      }

      const aDate = new Date(
        a.updatedAt || a.createdAt || 0
      ).getTime();

      const bDate = new Date(
        b.updatedAt || b.createdAt || 0
      ).getTime();

      return bDate - aDate;
    })[0];
  };

  /*
   * --------------------------------------------------------------------------
   * Duplicate group actions
   * --------------------------------------------------------------------------
   */

  const openCompare = (group) => {
    setSelectedGroup(group);
    setCompareVisible(true);
  };

  const handleKeepAndDeleteOthers = (group, keeper) => {
    const duplicatesToDelete =
      group.records.filter(
        (record) => record.id !== keeper.id
      );

    if (!duplicatesToDelete.length) {
      messageApi.info(
        'There are no duplicate records to remove.'
      );

      return;
    }

    Modal.confirm({
      title: 'Keep this customer record?',
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <div>
          <p>
            You are choosing this record as the primary
            customer record:
          </p>

          <Card
            size="small"
            style={{ marginBottom: 12 }}
          >
            <Space align="start">
              <Avatar
                icon={<UserOutlined />}
              />

              <div>
                <Text strong>
                  {keeper.name || 'Unnamed customer'}
                </Text>

                <br />

                <Text type="secondary">
                  ID: {keeper.id}
                </Text>

                <br />

                <Text type="secondary">
                  TIN: {keeper.tin || 'Not available'}
                </Text>

                <br />

                <Text type="secondary">
                  Phone: {keeper.phone || 'Not available'}
                </Text>
              </div>
            </Space>
          </Card>

          <Alert
            type="warning"
            showIcon
            message={`This will permanently delete ${duplicatesToDelete.length} duplicate record(s).`}
            description="This action should only be used when you are certain the other records are duplicates."
          />
        </div>
      ),
      okText: `Keep & Delete ${duplicatesToDelete.length}`,
      okType: 'danger',
      cancelText: 'Cancel',

      onOk: async () => {
        try {
          for (const record of duplicatesToDelete) {
            await api.delete(
              `/api/customers/${record.id}`
            );
          }

          messageApi.success(
            `Kept customer #${keeper.id} and removed ${duplicatesToDelete.length} duplicate record(s).`
          );

          setCompareVisible(false);
          setSelectedGroup(null);

          await Promise.all([
            fetchCustomers(),
            fetchDuplicates(false),
          ]);
        } catch (err) {
          console.error(
            'Failed to remove duplicates:',
            err
          );

          messageApi.error(
            'Some duplicate records could not be removed.'
          );
        }
      },
    });
  };

  /*
   * --------------------------------------------------------------------------
   * Export
   * --------------------------------------------------------------------------
   */

  const exportToExcel = () => {
    if (!customers.length) {
      messageApi.info(
        'There are no customers to export.'
      );

      return;
    }

    const data = customers.map((customer) => ({
      'Customer ID': customer.id,
      Name: customer.name,
      TIN: customer.tin || '',
      Phone: customer.phone || '',
      Email: customer.email || '',
      Address: customer.address || '',
      'Created At': customer.createdAt
        ? new Date(
            customer.createdAt
          ).toLocaleString()
        : '',
      'Updated At': customer.updatedAt
        ? new Date(
            customer.updatedAt
          ).toLocaleString()
        : '',
    }));

    const worksheet =
      XLSX.utils.json_to_sheet(data);

    worksheet['!cols'] = [
      { wch: 14 },
      { wch: 32 },
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
      { wch: 40 },
      { wch: 22 },
      { wch: 22 },
    ];

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Customers'
    );

    const excelBuffer = XLSX.write(
      workbook,
      {
        bookType: 'xlsx',
        type: 'array',
      }
    );

    const blob = new Blob(
      [excelBuffer],
      {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }
    );

    saveAs(
      blob,
      `Customers_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  /*
   * --------------------------------------------------------------------------
   * Duplicate type helpers
   * --------------------------------------------------------------------------
   */

  const getTypeColor = (type) => {
    switch (type) {
      case 'name':
        return 'red';

      case 'tin':
        return 'orange';

      case 'phone':
        return 'gold';

      default:
        return 'blue';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'name':
        return 'Name';

      case 'tin':
        return 'TIN';

      case 'phone':
        return 'Phone';

      default:
        return 'Duplicate';
    }
  };

  /*
   * --------------------------------------------------------------------------
   * Record field component
   * --------------------------------------------------------------------------
   */

  const RecordField = ({
    label,
    value,
    important = false,
  }) => {
    return (
      <div
        style={{
          padding: '8px 0',
          borderBottom:
            '1px solid #f0f0f0',
        }}
      >
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            display: 'block',
            marginBottom: 3,
          }}
        >
          {label}
        </Text>

        {value ? (
          <Text strong={important}>
            {value}
          </Text>
        ) : (
          <Text type="secondary">
            Not provided
          </Text>
        )}
      </div>
    );
  };

  /*
   * --------------------------------------------------------------------------
   * Duplicate record card
   * --------------------------------------------------------------------------
   */

  const DuplicateRecordCard = ({
    record,
    recommended,
    onEdit,
    onDelete,
    onKeep,
  }) => {
    const score =
      getCompletenessScore(record);

    return (
      <Card
        size="small"
        style={{
          height: '100%',
          borderRadius: 10,
          border: recommended
            ? '1px solid #52c41a'
            : '1px solid #f0f0f0',
          background: recommended
            ? '#f6ffed'
            : '#fff',
        }}
        bodyStyle={{
          padding: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent:
              'space-between',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Space align="start">
            <Avatar
              size={40}
              icon={<UserOutlined />}
            />

            <div>
              <Text strong>
                {record.name ||
                  'Unnamed Customer'}
              </Text>

              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  Record #{record.id}
                </Text>
              </div>
            </div>
          </Space>

          {recommended && (
            <Tag
              color="success"
              icon={
                <CheckCircleOutlined />
              }
            >
              Most Complete
            </Tag>
          )}
        </div>

        <Divider
          style={{
            margin: '12px 0',
          }}
        />

        <RecordField
          label="Customer Name"
          value={record.name}
          important
        />

        <RecordField
          label="TIN"
          value={record.tin}
        />

        <RecordField
          label="Phone"
          value={record.phone}
        />

        {record.email && (
          <RecordField
            label="Email"
            value={record.email}
          />
        )}

        {record.address && (
          <RecordField
            label="Address"
            value={record.address}
          />
        )}

        <div
          style={{
            marginTop: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent:
                'space-between',
              marginBottom: 5,
            }}
          >
            <Text
              type="secondary"
              style={{
                fontSize: 12,
              }}
            >
              Data completeness
            </Text>

            <Text
              type="secondary"
              style={{
                fontSize: 12,
              }}
            >
              {score} points
            </Text>
          </div>

          <Progress
            percent={Math.min(
              score * 10,
              100
            )}
            size="small"
            showInfo={false}
            status={
              recommended
                ? 'success'
                : 'normal'
            }
          />
        </div>

        <Space
          wrap
          style={{
            width: '100%',
          }}
        >
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() =>
              onEdit(record)
            }
          >
            Edit
          </Button>

          {!recommended && (
            <Button
              size="small"
              type="primary"
              ghost
              icon={
                <CheckCircleOutlined />
              }
              onClick={() =>
                onKeep(record)
              }
            >
              Keep
            </Button>
          )}

          <Popconfirm
            title="Delete this customer?"
            description="This action cannot be undone."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{
              danger: true,
            }}
            onConfirm={() =>
              onDelete(record.id)
            }
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      </Card>
    );
  };

  /*
   * --------------------------------------------------------------------------
   * Duplicate group card
   * --------------------------------------------------------------------------
   */

  const DuplicateGroupCard = ({
    group,
  }) => {
    const recommended =
      getMostCompleteRecord(
        group.records
      );

    const isExpanded =
      expandedGroups.includes(group.key);

    return (
      <Card
        style={{
          marginBottom: 12,
          borderRadius: 12,
          overflow: 'hidden',
        }}
        bodyStyle={{
          padding: 0,
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            cursor: 'pointer',
            background: isExpanded
              ? '#fafafa'
              : '#fff',
          }}
          onClick={() => {
            setExpandedGroups(
              (current) =>
                current.includes(group.key)
                  ? current.filter(
                      (key) =>
                        key !== group.key
                    )
                  : [
                      ...current,
                      group.key,
                    ]
            );
          }}
        >
          <Row
            gutter={[
              16,
              12,
            ]}
            align="middle"
          >
            <Col
              xs={24}
              md={11}
            >
              <Space align="start">
                <Avatar
                  size={42}
                  icon={<UserOutlined />}
                />

                <div>
                  <Space wrap>
                    <Text
                      strong
                      style={{
                        fontSize: 16,
                      }}
                    >
                      {group.value ||
                        'Unknown'}
                    </Text>

                    <Tag
                      color={getTypeColor(
                        group.type
                      )}
                    >
                      Duplicate by{' '}
                      {getTypeLabel(
                        group.type
                      )}
                    </Tag>
                  </Space>

                  <div
                    style={{
                      marginTop: 4,
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 13,
                      }}
                    >
                      {group.records.length}{' '}
                      duplicate records
                    </Text>
                  </div>
                </div>
              </Space>
            </Col>

            <Col
              xs={12}
              md={5}
            >
              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  Recommended record
                </Text>

                <div>
                  <Text strong>
                    #{recommended?.id}
                  </Text>

                  <Tag
                    color="success"
                    style={{
                      marginLeft: 8,
                    }}
                  >
                    Most Complete
                  </Tag>
                </div>
              </div>
            </Col>

            <Col
              xs={12}
              md={4}
            >
              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  Records affected
                </Text>

                <div>
                  <Text strong>
                    {group.records.length}
                  </Text>
                </div>
              </div>
            </Col>

            <Col
              xs={24}
              md={4}
              style={{
                textAlign: 'right',
              }}
            >
              <Button
                size="small"
                icon={
                  isExpanded ? (
                    <CloseOutlined />
                  ) : (
                    <EyeOutlined />
                  )
                }
                onClick={(event) => {
                  event.stopPropagation();

                  setExpandedGroups(
                    (current) =>
                      current.includes(
                        group.key
                      )
                        ? current.filter(
                            (key) =>
                              key !==
                              group.key
                          )
                        : [
                            ...current,
                            group.key,
                          ]
                  );
                }}
              >
                {isExpanded
                  ? 'Close'
                  : 'Review'}
              </Button>
            </Col>
          </Row>
        </div>

        {isExpanded && (
          <div
            style={{
              padding: 20,
              borderTop:
                '1px solid #f0f0f0',
              background: '#fafafa',
            }}
          >
            <Row
              gutter={[
                16,
                16,
              ]}
            >
              {group.records.map(
                (record) => (
                  <Col
                    key={record.id}
                    xs={24}
                    lg={
                      group.records.length ===
                      2
                        ? 12
                        : 8
                    }
                  >
                    <DuplicateRecordCard
                      record={record}
                      recommended={
                        recommended?.id ===
                        record.id
                      }
                      onEdit={
                        handleEdit
                      }
                      onDelete={
                        handleDelete
                      }
                      onKeep={(keeper) =>
                        handleKeepAndDeleteOthers(
                          group,
                          keeper
                        )
                      }
                    />
                  </Col>
                )
              )}
            </Row>

            <div
              style={{
                marginTop: 16,
              }}
            >
              <Alert
                type="info"
                showIcon
                message="Review the records before removing duplicates"
                description="The green record is automatically identified as the most complete based on available customer information. You can choose another record as the one to keep."
              />
            </div>

            <div
              style={{
                marginTop: 16,
                display: 'flex',
                justifyContent:
                  'flex-end',
              }}
            >
              <Button
                type="primary"
                danger
                icon={
                  <MergeCellsOutlined />
                }
                onClick={() =>
                  openCompare(group)
                }
              >
                Review & Resolve Group
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  /*
   * --------------------------------------------------------------------------
   * All customer table
   * --------------------------------------------------------------------------
   */

  const customerColumns = [
    {
      title: 'Customer',
      key: 'customer',
      fixed: 'left',
      width: 260,
      render: (_, record) => (
        <Space>
          <Avatar
            size="small"
            icon={<UserOutlined />}
          />

          <div>
            <Text strong>
              {record.name ||
                'Unnamed Customer'}
            </Text>

            <div>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                ID #{record.id}
              </Text>
            </div>
          </div>
        </Space>
      ),
    },

    {
      title: 'TIN',
      dataIndex: 'tin',
      key: 'tin',
      width: 160,
      render: (tin) =>
        tin || (
          <Text type="secondary">
            —
          </Text>
        ),
    },

    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 180,
      render: (phone) =>
        phone || (
          <Text type="secondary">
            —
          </Text>
        ),
    },

    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (date) =>
        date
          ? new Date(
              date
            ).toLocaleString()
          : '—',
    },

    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (date) =>
        date
          ? new Date(
              date
            ).toLocaleString()
          : '—',
    },

    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit customer">
            <Button
              size="small"
              icon={
                <EditOutlined />
              }
              onClick={() =>
                handleEdit(record)
              }
            />
          </Tooltip>

          <Popconfirm
            title="Delete customer?"
            description="This action cannot be undone."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{
              danger: true,
            }}
            onConfirm={() =>
              handleDelete(
                record.id
              )
            }
          >
            <Tooltip title="Delete customer">
              <Button
                size="small"
                danger
                icon={
                  <DeleteOutlined />
                }
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /*
   * --------------------------------------------------------------------------
   * Compare modal
   * --------------------------------------------------------------------------
   */

  const CompareModal = () => {
    if (!selectedGroup) {
      return null;
    }

    const recommended =
      getMostCompleteRecord(
        selectedGroup.records
      );

    return (
      <Modal
        title={
          <Space>
            <MergeCellsOutlined />

            <span>
              Review Duplicate Group
            </span>
          </Space>
        }
        open={compareVisible}
        onCancel={() => {
          setCompareVisible(false);
          setSelectedGroup(null);
        }}
        footer={null}
        width={1000}
        centered
      >
        <Alert
          type="warning"
          showIcon
          style={{
            marginBottom: 20,
          }}
          message={`Found ${selectedGroup.records.length} possible duplicate records`}
          description={`Matched by ${getTypeLabel(
            selectedGroup.type
          )}: ${selectedGroup.value}`}
        />

        <Row
          gutter={[
            16,
            16,
          ]}
        >
          {selectedGroup.records.map(
            (record) => {
              const isRecommended =
                recommended?.id ===
                record.id;

              return (
                <Col
                  xs={24}
                  md={
                    selectedGroup.records
                      .length === 2
                      ? 12
                      : 8
                  }
                  key={record.id}
                >
                  <DuplicateRecordCard
                    record={record}
                    recommended={
                      isRecommended
                    }
                    onEdit={
                      handleEdit
                    }
                    onDelete={
                      handleDelete
                    }
                    onKeep={(keeper) =>
                      handleKeepAndDeleteOthers(
                        selectedGroup,
                        keeper
                      )
                    }
                  />
                </Col>
              );
            }
          )}
        </Row>
      </Modal>
    );
  };

  /*
   * --------------------------------------------------------------------------
   * Main UI
   * --------------------------------------------------------------------------
   */

  return (
    <div

    >
      {contextHolder}

      <div
     
      >
        {/* Breadcrumb */}
     

        {/* Header */}
        <Card
          bordered={false}
          style={{
            borderRadius: 14,
            marginBottom: 16,
          }}
          bodyStyle={{
            padding:
              '22px 24px',
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            gutter={[
              16,
              16,
            ]}
          >
            <Col>
              <Space align="start">
                <Avatar
                  size={48}
                  icon={<TeamOutlined />}
                  style={{
                    background:
                      '#e6f4ff',
                    color:
                      '#1677ff',
                  }}
                />

                <div>
                  <Title
                    level={3}
                    style={{
                      margin: 0,
                    }}
                  >
                    Customer Management
                  </Title>

                  <Text type="secondary">
                    Manage customer records,
                    identify duplicates and
                    maintain clean data.
                  </Text>
                </div>
              </Space>
            </Col>

            <Col>
              <Space wrap>
                <Button
                  icon={
                    <DownloadOutlined />
                  }
                  onClick={
                    exportToExcel
                  }
                  disabled={
                    !customers.length
                  }
                >
                  Export
                </Button>

                <Button
                  type="primary"
                  icon={
                    <PlusOutlined />
                  }
                  onClick={
                    handleCreate
                  }
                >
                  Add Customer
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Statistics */}
        <Row
          gutter={[
            16,
            16,
          ]}
          style={{
            marginBottom: 16,
          }}
        >
          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Total Customers"
                value={
                  customers.length
                }
                prefix={
                  <TeamOutlined />
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Customer records
              </Text>
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Duplicate Groups"
                value={
                  totalDuplicateGroups
                }
                valueStyle={{
                  color:
                    totalDuplicateGroups >
                    0
                      ? '#cf1322'
                      : '#389e0d',
                }}
                prefix={
                  <WarningOutlined />
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Groups requiring review
              </Text>
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Records Affected"
                value={
                  totalAffectedRecords
                }
                valueStyle={{
                  color:
                    totalAffectedRecords >
                    0
                      ? '#d48806'
                      : '#389e0d',
                }}
                prefix={
                  <ExclamationCircleOutlined />
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Unique customer records
              </Text>
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
              }}
            >
              <Statistic
                title="Data Quality"
                value={
                  totalDuplicateGroups
                    ? 'Review Needed'
                    : 'Clean'
                }
                valueStyle={{
                  color:
                    totalDuplicateGroups
                      ? '#d48806'
                      : '#389e0d',
                  fontSize: 20,
                }}
                prefix={
                  totalDuplicateGroups ? (
                    <WarningOutlined />
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
              />

              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                Current duplicate status
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Main workspace */}
        <Card
          bordered={false}
          style={{
            borderRadius: 14,
          }}
          bodyStyle={{
            padding: 0,
          }}
        >
          {/* Workspace tabs */}
          <div
            style={{
              borderBottom:
                '1px solid #f0f0f0',
              padding:
                '0 20px',
            }}
          >
            <Space
              size={4}
              style={{
                marginTop: 4,
              }}
            >
              <Button
                type={
                  activeTab ===
                  'duplicates'
                    ? 'primary'
                    : 'text'
                }
                danger={
                  activeTab ===
                  'duplicates'
                }
                icon={
                  <ExclamationCircleOutlined />
                }
                onClick={() =>
                  setActiveTab(
                    'duplicates'
                  )
                }
              >
                Duplicate Audit

                {totalDuplicateGroups >
                  0 && (
                  <Badge
                    count={
                      totalDuplicateGroups
                    }
                    overflowCount={
                      9999
                    }
                    style={{
                      marginLeft: 6,
                    }}
                  />
                )}
              </Button>

              <Button
                type={
                  activeTab ===
                  'customers'
                    ? 'primary'
                    : 'text'
                }
                icon={
                  <TeamOutlined />
                }
                onClick={() =>
                  setActiveTab(
                    'customers'
                  )
                }
              >
                All Customers

                <Text
                  type="secondary"
                  style={{
                    marginLeft: 6,
                  }}
                >
                  {customers.length}
                </Text>
              </Button>
            </Space>
          </div>

          {/* Duplicate workspace */}
          {activeTab ===
            'duplicates' && (
            <div
              style={{
                padding: 20,
              }}
            >
              {totalDuplicateGroups >
                0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{
                    marginBottom: 16,
                  }}
                  message={`${totalDuplicateGroups.toLocaleString()} duplicate groups require review`}
                  description="Review each group and keep the most complete customer record. Only delete records when you are certain they are duplicates."
                />
              )}

              {/* Filter toolbar */}
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  background:
                    '#fafafa',
                  borderRadius: 10,
                }}
              >
                <Row
                  gutter={[
                    12,
                    12,
                  ]}
                  align="middle"
                >
                  <Col
                    xs={24}
                    md={10}
                    lg={9}
                  >
                    <Input
                      allowClear
                      size="large"
                      prefix={
                        <SearchOutlined />
                      }
                      placeholder="Search customer name, TIN, phone or ID..."
                      value={
                        duplicateSearch
                      }
                      onChange={(e) => {
                        setDuplicateSearch(
                          e.target.value
                        );
                        setDuplicatePage(
                          1
                        );
                      }}
                    />
                  </Col>

                  <Col
                    xs={12}
                    md={5}
                    lg={4}
                  >
                    <Select
                      size="large"
                      style={{
                        width: '100%',
                      }}
                      value={
                        duplicateType
                      }
                      onChange={(value) => {
                        setDuplicateType(
                          value
                        );
                        setDuplicatePage(
                          1
                        );
                      }}
                      options={[
                        {
                          value:
                            'all',
                          label:
                            'All Types',
                        },
                        {
                          value:
                            'name',
                          label:
                            'Duplicate Name',
                        },
                        {
                          value:
                            'tin',
                          label:
                            'Duplicate TIN',
                        },
                        {
                          value:
                            'phone',
                          label:
                            'Duplicate Phone',
                        },
                      ]}
                    />
                  </Col>

                  <Col
                    xs={12}
                    md={5}
                    lg={4}
                  >
                    <Select
                      size="large"
                      style={{
                        width: '100%',
                      }}
                      value={
                        duplicateSize
                      }
                      onChange={(value) => {
                        setDuplicateSize(
                          value
                        );
                        setDuplicatePage(
                          1
                        );
                      }}
                      options={[
                        {
                          value:
                            'all',
                          label:
                            'Any Group Size',
                        },
                        {
                          value:
                            '2',
                          label:
                            'Exactly 2 Records',
                        },
                        {
                          value:
                            '3+',
                          label:
                            '3+ Records',
                        },
                      ]}
                    />
                  </Col>

                  <Col
                    xs={24}
                    md={4}
                    lg={7}
                    style={{
                      textAlign:
                        'right',
                    }}
                  >
                    <Space wrap>
                      <Tooltip title="Refresh duplicate audit">
                        <Button
                          size="large"
                          icon={
                            <ReloadOutlined />
                          }
                          loading={
                            duplicateLoading
                          }
                          onClick={() =>
                            fetchDuplicates()
                          }
                        >
                          Refresh
                        </Button>
                      </Tooltip>

                      <Button
                        size="large"
                        icon={
                          <FilterOutlined />
                        }
                        onClick={() => {
                          setDuplicateSearch(
                            ''
                          );
                          setDuplicateType(
                            'all'
                          );
                          setDuplicateSize(
                            'all'
                          );
                          setDuplicatePage(
                            1
                          );
                        }}
                      >
                        Reset
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* Duplicate type summary */}
              <Row
                gutter={[
                  12,
                  12,
                ]}
                style={{
                  marginBottom: 16,
                }}
              >
                <Col
                  xs={24}
                  sm={8}
                >
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                    }}
                  >
                    <Space>
                      <Tag color="red">
                        NAME
                      </Tag>

                      <div>
                        <Text strong>
                          {
                            nameDuplicateGroups
                          }
                        </Text>

                        <div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            duplicate groups
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>

                <Col
                  xs={24}
                  sm={8}
                >
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                    }}
                  >
                    <Space>
                      <Tag color="orange">
                        TIN
                      </Tag>

                      <div>
                        <Text strong>
                          {
                            tinDuplicateGroups
                          }
                        </Text>

                        <div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            duplicate groups
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>

                <Col
                  xs={24}
                  sm={8}
                >
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                    }}
                  >
                    <Space>
                      <Tag color="gold">
                        PHONE
                      </Tag>

                      <div>
                        <Text strong>
                          {
                            phoneDuplicateGroups
                          }
                        </Text>

                        <div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            duplicate groups
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>

              {/* Results header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'center',
                  marginBottom: 12,
                  flexWrap:
                    'wrap',
                  gap: 8,
                }}
              >
                <div>
                  <Text strong>
                    Duplicate Groups
                  </Text>

                  <Text
                    type="secondary"
                    style={{
                      marginLeft: 8,
                    }}
                  >
                    {filteredDuplicateGroups.length.toLocaleString()}{' '}
                    groups
                  </Text>
                </div>

                {filteredDuplicateGroups.length >
                  0 && (
                  <Text type="secondary">
                    Showing{' '}
                    {Math.min(
                      (duplicatePage -
                        1) *
                        PAGE_SIZE +
                        1,
                      filteredDuplicateGroups.length
                    )}
                    –
                    {Math.min(
                      duplicatePage *
                        PAGE_SIZE,
                      filteredDuplicateGroups.length
                    )}{' '}
                    of{' '}
                    {
                      filteredDuplicateGroups.length
                    }
                  </Text>
                )}
              </div>

              {/* Duplicate groups */}
              {duplicateLoading ? (
                <div
                  style={{
                    padding: 80,
                    textAlign:
                      'center',
                  }}
                >
                  <Progress
                    type="circle"
                    percent={70}
                    status="active"
                  />

                  <div
                    style={{
                      marginTop: 16,
                    }}
                  >
                    <Text type="secondary">
                      Analyzing customer
                      records...
                    </Text>
                  </div>
                </div>
              ) : paginatedDuplicateGroups.length >
                0 ? (
                <>
                  {paginatedDuplicateGroups.map(
                    (group) => (
                      <DuplicateGroupCard
                        key={
                          group.key
                        }
                        group={group}
                      />
                    )
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent:
                        'center',
                      padding:
                        '20px 0 4px',
                    }}
                  >
                    <Pagination
                      current={
                        duplicatePage
                      }
                      pageSize={
                        PAGE_SIZE
                      }
                      total={
                        filteredDuplicateGroups.length
                      }
                      showSizeChanger={
                        false
                      }
                      showQuickJumper
                      showTotal={(
                        total,
                        range
                      ) =>
                        `${range[0]}-${range[1]} of ${total} groups`
                      }
                      onChange={(
                        page
                      ) => {
                        setDuplicatePage(
                          page
                        );

                        window.scrollTo(
                          {
                            top: 0,
                            behavior:
                              'smooth',
                          }
                        );
                      }}
                    />
                  </div>
                </>
              ) : (
                <Empty
                  image={
                    Empty.PRESENTED_IMAGE_SIMPLE
                  }
                  description={
                    totalDuplicateGroups ===
                    0
                      ? 'No duplicate customers found'
                      : 'No duplicate groups match your filters'
                  }
                >
                  {duplicateSearch ||
                  duplicateType !==
                    'all' ||
                  duplicateSize !==
                    'all' ? (
                    <Button
                      onClick={() => {
                        setDuplicateSearch(
                          ''
                        );
                        setDuplicateType(
                          'all'
                        );
                        setDuplicateSize(
                          'all'
                        );
                        setDuplicatePage(
                          1
                        );
                      }}
                    >
                      Clear Filters
                    </Button>
                  ) : null}
                </Empty>
              )}
            </div>
          )}

          {/* All customers */}
          {activeTab ===
            'customers' && (
            <div
              style={{
                padding: 20,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'center',
                  marginBottom: 16,
                  flexWrap:
                    'wrap',
                  gap: 12,
                }}
              >
                <div>
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                    }}
                  >
                    All Customers
                  </Title>

                  <Text type="secondary">
                    Browse and manage all
                    customer records.
                  </Text>
                </div>

                <Button
                  icon={
                    <PlusOutlined />
                  }
                  type="primary"
                  onClick={
                    handleCreate
                  }
                >
                  Add Customer
                </Button>
              </div>

              <Table
                columns={
                  customerColumns
                }
                dataSource={
                  customers
                }
                rowKey="id"
                loading={
                  loading
                }
                pagination={{
                  pageSize: 20,
                  showSizeChanger:
                    true,
                  showQuickJumper:
                    true,
                  showTotal: (
                    total,
                    range
                  ) =>
                    `${range[0]}-${range[1]} of ${total} customers`,
                  pageSizeOptions: [
                    10,
                    20,
                    50,
                    100,
                  ],
                }}
                scroll={{
                  x: 1000,
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description="No customers found"
                    />
                  ),
                }}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <Space>
            <Avatar
              icon={<UserOutlined />}
              style={{
                background:
                  '#e6f4ff',
                color:
                  '#1677ff',
              }}
            />

            <span>
              {editingCustomer
                ? 'Edit Customer'
                : 'Add Customer'}
            </span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCustomer(
            null
          );
          form.resetFields();
        }}
        onOk={() =>
          form.submit()
        }
        okText={
          editingCustomer
            ? 'Save Changes'
            : 'Create Customer'
        }
        centered
        width={620}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={
            handleSubmit
          }
        >
          <Alert
            type="info"
            showIcon
            message="Customer information"
            description="Provide as much information as possible. Complete records make duplicate identification and customer management easier."
            style={{
              marginBottom: 20,
            }}
          />

          <Row
            gutter={[
              16,
              0,
            ]}
          >
            <Col span={24}>
              <Form.Item
                name="name"
                label="Customer Name"
                rules={[
                  {
                    required: true,
                    message:
                      'Please enter customer name',
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter customer name"
                  prefix={
                    <UserOutlined />
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="tin"
                label="TIN"
              >
                <Input
                  size="large"
                  placeholder="Enter TIN"
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                name="phone"
                label="Phone"
              >
                <Input
                  size="large"
                  placeholder="Enter phone number"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="email"
                label="Email"
              >
                <Input
                  size="large"
                  placeholder="Enter email address"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="address"
                label="Address"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter customer address"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Compare / Resolve Modal */}
      <CompareModal />
    </div>
  );
};

export default CustomerManagement;
